import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FileText, Upload, CheckCircle, XCircle, Clock, Eye } from "lucide-react";
import { toast } from "react-hot-toast";
import { FournisseurService } from "../../services/fournisseurs/fournisseurService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { DocumentViewerModal } from "../../components/common/Modals/DocumentViewerModal";

const DOCUMENT_TYPES = [
  { value: "rccm", label: "RCCM" },
  { value: "ninea", label: "NINEA" },
  { value: "attestation_fiscale", label: "Attestation Fiscale" },
  { value: "iban", label: "Relevé IBAN" },
  { value: "identite", label: "Pièce d'identité" },
  { value: "autre", label: "Autre" },
];

interface Document {
  id: string;
  type_document: string;
  nom_fichier: string;
  taille_fichier: number;
  statut: "en_attente" | "approuve" | "rejete";
  created_at: string;
  fichier: string;
}

export const FournisseurDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState(DOCUMENT_TYPES[0].value);
  const [file, setFile] = useState<File | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{url: string, title: string} | null>(null);

  const fetchDocuments = async () => {
    try {
      const response = await FournisseurService.getDocuments();
      setDocuments(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement documents", error);
      toast.error("Impossible de charger les documents");
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("Le fichier est trop volumineux (max 10Mo)");
        return;
      }
      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    const formData = new FormData();
    formData.append("type_document", selectedType);
    formData.append("fichier", file);

    try {
      setUploading(true);
      await FournisseurService.uploadDocument(formData);
      toast.success("Document téléchargé avec succès");
      setFile(null);
      fetchDocuments();
    } catch (error) {
      console.error("Erreur upload", error);
      toast.error("Erreur lors du téléchargement du document");
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (statut: string) => {
    switch (statut) {
      case "approuve":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" /> Approuvé
          </span>
        );
      case "rejete":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" /> Rejeté
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" /> En attente
          </span>
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getTypeLabel = (type: string) => {
    return DOCUMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez les documents légaux de votre entreprise
          </p>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Ajouter un document
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label htmlFor="document-type" className="block text-sm font-medium text-gray-700">
                Type de document
              </label>
              <div className="mt-1">
                <select
                  id="document-type"
                  name="document-type"
                  className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sm:col-span-6">
              <div
                {...getRootProps()}
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors cursor-pointer ${
                  isDragActive
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300 hover:border-primary-400"
                }`}
              >
                <div className="space-y-1 text-center">
                  <input {...getInputProps()} />
                  {file ? (
                    <div className="flex flex-col items-center">
                      <FileText className="mx-auto h-12 w-12 text-primary-500" />
                      <p className="mt-2 text-sm text-gray-900 font-medium">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Supprimer
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600 justify-center">
                        <span className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                          Téléverser un fichier
                        </span>
                        <p className="pl-1">ou glisser-déposer</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PDF, PNG, JPG jusqu'à 10Mo
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
             <button
                type="button"
                onClick={handleUpload}
                disabled={!file || uploading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {uploading ? "Envoi..." : "Envoyer"}
              </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Documents envoyés
          </h3>
        </div>
        
        {documents.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            Aucun document téléchargé pour le moment.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <li key={doc.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="ml-4">
                        <div className="text-sm font-medium text-primary-600 truncate">
                            {doc.nom_fichier}
                        </div>
                        <div className="text-sm text-gray-500">
                            {getTypeLabel(doc.type_document)} • {formatFileSize(doc.taille_fichier)}
                        </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                      <div className="mr-4">
                        {getStatusBadge(doc.statut)}
                      </div>
                      <div className="text-sm text-gray-500 mr-4">
                        {format(new Date(doc.created_at), "dd MMM yyyy", { locale: fr })}
                      </div>
                      {doc.fichier && (
                          <button 
                            onClick={() => setViewingDocument({ url: doc.fichier, title: doc.nom_fichier })} 
                            className="text-gray-400 hover:text-gray-500"
                            title="Voir le document"
                          >
                              <Eye className="h-5 w-5" />
                          </button>
                      )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {viewingDocument && (
        <DocumentViewerModal
            url={viewingDocument.url}
            title={viewingDocument.title}
            onClose={() => setViewingDocument(null)}
        />
      )}
    </div>
  );
};
